import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Plus, X, MapPin, LocateFixed, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import LocationAutocomplete from './LocationAutocomplete';
import MapPinSelector from './MapPinSelector';
import type { NoteLocation, PromoItem } from '@/types/location';
import { cn } from '@/lib/utils';
import { getReverseGeocodeCity } from '@/lib/georeverse';

interface Coupon {
  code: string;
  description?: string;
}

interface PromoDetailsPanelProps {
  storeName?: string;
  onStoreNameChange: (name: string) => void;
  
  vendorLocation?: NoteLocation | null;
  onVendorLocationChange: (location: NoteLocation | null) => void;
  
  items?: PromoItem[];
  onItemsChange: (items: PromoItem[]) => void;
  
  externalUrl?: string;
  onExternalUrlChange: (url: string) => void;
  
  coupons?: Coupon[];
  onCouponsChange: (coupons: Coupon[]) => void;
  
  isAffiliated?: boolean;
  onIsAffiliatedChange: (value: boolean) => void;
  
  isFreebee?: boolean;
  onIsFreebeeChange: (value: boolean) => void;
  
  startAt?: string;
  onStartAtChange: (date: string) => void;
  
  expiryAt?: string;
  onExpiryAtChange: (date: string) => void;
  
  disabled?: boolean;
}

export default function PromoDetailsPanel({
  storeName = '',
  onStoreNameChange,
  vendorLocation,
  onVendorLocationChange,
  items = [],
  onItemsChange,
  externalUrl = '',
  onExternalUrlChange,
  coupons = [],
  onCouponsChange,
  isAffiliated = false,
  onIsAffiliatedChange,
  isFreebee = false,
  onIsFreebeeChange,
  startAt = '',
  onStartAtChange,
  expiryAt = '',
  onExpiryAtChange,
  disabled = false,
}: PromoDetailsPanelProps) {
  const { toast } = useToast();
  
  // Location States (estilo RequestRideSidebar)
  const [locationExpanded, setLocationExpanded] = useState(false);
  const [inputLocation, setInputLocation] = useState<NoteLocation | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [refining, setRefining] = useState(false);
  const [coords, setCoords] = useState<{lat: number, lon: number} | null>(null);
  const [cityName, setCityName] = useState<string | null>(null);
  const [locationDetails, setLocationDetails] = useState({
    streetName: '',
    altura: '',
    floor: '',
    apartment: '',
    notes: ''
  });
  const [showAlturaError, setShowAlturaError] = useState(false);

  // Items States
  const [newItemName, setNewItemName] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');

  // Sincronizar coordenadas iniciales si existe ubicación
  useEffect(() => {
    if (vendorLocation && !coords) {
      setCoords({ lat: vendorLocation.latitude, lon: vendorLocation.longitude });
    }
  }, [vendorLocation, coords]);

  // Obtener nombre de ciudad desde georeverse cuando vendorLocation cambia
  useEffect(() => {
    const fetchCity = async () => {
      if (vendorLocation) {
        try {
          const city = await getReverseGeocodeCity(vendorLocation.latitude, vendorLocation.longitude);
          setCityName(city);
        } catch (err) {
          console.error('[PromoDetailsPanel] Error fetching city:', err);
          setCityName(null);
        }
      }
    };
    fetchCity();
  }, [vendorLocation]);

  // Cuando se selecciona una dirección desde el autocomplete, abrir automáticamente el editor
  useEffect(() => {
    if (inputLocation && coords && !refining) {
      setRefining(true);
    }
  }, [inputLocation, coords]);

  const handleUseCurrentLocation = async () => {
    try {
      if (!navigator.geolocation) {
        toast({ title: 'Error', description: 'Geolocalización no disponible', variant: 'destructive' });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`, { mode: 'cors' });
            const j = await r.json();
            const displayName = j.display_name || 'Mi ubicación actual';
            const addr = j.address || {};
            const road = addr.road || addr.street || addr.pedestrian || '';
            const houseNumber = addr.house_number || '';
            
            setCoords({ lat: latitude, lon: longitude });
            setLocationDetails({
              streetName: road,
              altura: houseNumber,
              floor: '',
              apartment: '',
              notes: ''
            });
            setRefining(true);
          } catch (e) {
            toast({ title: 'Error', description: 'No se pudo obtener la dirección', variant: 'destructive' });
          }
        },
        () => toast({ title: 'Error', description: 'No se pudo obtener tu ubicación', variant: 'destructive' }),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleConfirmLocation = () => {
    if (!locationDetails.altura.trim()) {
      setShowAlturaError(true);
      toast({ title: 'Error', description: 'Debes ingresar la altura (número)', variant: 'destructive' });
      return;
    }

    if (coords) {
      // Usar el nombre de la calle y la altura para el display name resumido
      const addressBase = `${locationDetails.streetName} ${locationDetails.altura}`.trim();
      const floorApartment = [
        locationDetails.floor ? `Piso ${locationDetails.floor}` : null,
        locationDetails.apartment ? `Depto ${locationDetails.apartment}` : null
      ].filter(Boolean).join(' ');

      const fullAddress = [
        addressBase,
        floorApartment,
        locationDetails.notes ? `(${locationDetails.notes})` : null,
        inputLocation?.displayName?.split(',').slice(2).join(',').trim() || ''
      ].filter(Boolean).join(', ');
      
      onVendorLocationChange({
        latitude: coords.lat,
        longitude: coords.lon,
        displayName: fullAddress,
        road: locationDetails.streetName,
        houseNumber: locationDetails.altura,
      });
      setRefining(false);
    }
  };

  const extractAddressComponents = (displayName: string) => {
    const parts = displayName.split(',').map(p => p.trim());
    let streetName = '';
    let altura = '';

    const firstPart = parts[0];
    const match = firstPart.match(/^(.*?)\s+(\d+)$/);
    
    if (match) {
      streetName = match[1];
      altura = match[2];
    } else {
      streetName = firstPart;
      // Check if second part is just a number (altura)
      if (parts.length > 1 && /^\d+$/.test(parts[1])) {
        altura = parts[1];
      }
    }

    return { streetName, altura };
  };

  const handleSelectLocation = useCallback((loc: NoteLocation) => {
    const { streetName, altura } = extractAddressComponents(loc.displayName);
    setInputLocation(loc);
    setCoords({ lat: loc.latitude, lon: loc.longitude });
    setLocationDetails({
      streetName: loc.road || streetName,
      altura: loc.houseNumber || altura,
      floor: '',
      apartment: '',
      notes: ''
    });
    setIsTyping(false);
    // Forzamos el refinamiento inmediatamente aquí también por si el useEffect falla en dispararse a tiempo
    setRefining(true);
  }, []);

  const handleAddItem = useCallback(() => {
    if (!newItemName.trim() || !newItemPrice.trim()) {
      toast({
        title: 'Error',
        description: 'Completa nombre y precio del item',
        variant: 'destructive',
      });
      return;
    }

    const price = parseFloat(newItemPrice);
    if (isNaN(price) || price <= 0) {
      toast({
        title: 'Error',
        description: 'El precio debe ser un número válido mayor a 0',
        variant: 'destructive',
      });
      return;
    }

    const newItem: PromoItem = {
      name: newItemName.trim(),
      price,
      quantity: 1,
    };

    onItemsChange([...items, newItem]);
    setNewItemName('');
    setNewItemPrice('');
  }, [newItemName, newItemPrice, items, onItemsChange, toast]);

  const handleUpdateItemQuantity = useCallback(
    (index: number, quantity: number) => {
      const updated = [...items];
      updated[index] = { ...updated[index], quantity: Math.max(1, quantity) };
      onItemsChange(updated);
    },
    [items, onItemsChange]
  );

  const totalPrice = items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);

  const truncateAddress = (displayName: string) => {
    if (!displayName) return '';
    const parts = displayName.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]} ${parts[1] || ''}`.trim();
    }
    return parts[0] || '';
  };

  return (
    <div className="space-y-6">
      {/* Ubicación */}
      <div className="space-y-4">
        <Label className="text-sm font-semibold">Local y Ubicación *</Label>

        <Input
          id="store-name"
          value={storeName}
          onChange={(e) => onStoreNameChange(e.target.value)}
          placeholder="Nombre del local"
          disabled={disabled}
          className="bg-background h-10"
        />
        
        {vendorLocation && !refining && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center justify-between border rounded-lg p-3 bg-background"
          >
            <div className="flex-1">
              <div className="font-bold text-sm flex items-center gap-2">
                <MapPin className="w-3 h-3 text-primary" />
                Publicado desde {cityName || 'tu ubicación'}
              </div>
              <div className="text-[10px] text-muted-foreground truncate">
                ({vendorLocation.displayName})
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => { setRefining(true); }}>
              Cambiar
            </Button>
          </motion.div>
        )}

        {!vendorLocation || refining ? (
          <div className="space-y-4">
            {refining && coords ? (
              <motion.div 
                key="refining"
                layout
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="p-4 border rounded-lg space-y-4 bg-background shadow-sm"
              >
                <div className="rounded-lg overflow-hidden border" style={{ height: 200 }}>
                  <MapPinSelector
                    lat={coords.lat}
                    lon={coords.lon}
                    onChange={(lat, lon) => setCoords({ lat, lon })}
                    height="200px"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Calle</Label>
                    <Input 
                      value={locationDetails.streetName} 
                      onChange={(e) => setLocationDetails(prev => ({ ...prev, streetName: e.target.value }))}
                      className="h-10 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className={cn("text-xs", showAlturaError && "text-primary font-bold")}>Altura *</Label>
                    <Input 
                      value={locationDetails.altura} 
                      onChange={(e) => {
                        setLocationDetails(prev => ({ ...prev, altura: e.target.value }));
                        if (e.target.value.trim()) setShowAlturaError(false);
                      }}
                      placeholder="Nro"
                      inputMode="numeric"
                      className={cn("h-10 text-sm", showAlturaError && "border-primary border-2")}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Input 
                    value={locationDetails.floor} 
                    onChange={(e) => setLocationDetails(prev => ({ ...prev, floor: e.target.value }))}
                    placeholder="Piso"
                    className="h-10 text-sm"
                  />
                  <Input 
                    value={locationDetails.apartment} 
                    onChange={(e) => setLocationDetails(prev => ({ ...prev, apartment: e.target.value }))}
                    placeholder="Depto"
                    className="h-10 text-sm"
                  />
                </div>

                <Input 
                  value={locationDetails.notes} 
                  onChange={(e) => setLocationDetails(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Indicaciones (portón, timbre...)"
                  className="h-10 text-sm"
                />

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setRefining(false)}>
                    Cancelar
                  </Button>
                  <Button className="flex-1" onClick={handleConfirmLocation}>
                    Confirmar
                  </Button>
                </div>
              </motion.div>
            ) : (
              <motion.div key="search" layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <div className="relative">
                  <LocationAutocomplete
                    placeholder="Buscar dirección..."
                    onSelect={(loc) => {
                      handleSelectLocation(loc);
                    }}
                    onTypingStateChange={setIsTyping}
                  />
                  {!isTyping && <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground opacity-50 pointer-events-none" />}
                </div>

                {!isTyping && (
                  <Button
                    variant="outline"
                    className="w-full h-12 text-sm font-medium border-dashed border-2 border-primary/20"
                    onClick={handleUseCurrentLocation}
                  >
                    <LocateFixed className="w-5 h-5 mr-2" />
                    Mi ubicación actual
                  </Button>
                )}
              </motion.div>
            )}
          </div>
        ) : null}
      </div>

      {/* Items */}
      <div className="space-y-4 border-t pt-4">
        <Label className="text-sm font-semibold">Items y Precios</Label>
        
        <div className="flex gap-2">
          <Input placeholder="Producto" value={newItemName} onChange={(e) => setNewItemName(e.target.value)} className="flex-1" />
          <Input placeholder="$" value={newItemPrice} onChange={(e) => setNewItemPrice(e.target.value)} className="w-20" type="number" />
          <Button size="icon" onClick={handleAddItem} className="w-10"><Plus className="w-5 h-5" /></Button>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div className="p-3 border-b bg-muted/20 grid grid-cols-[1fr,80px,40px] gap-2 text-[10px] font-bold text-muted-foreground">
            <div>Producto</div>
            <div className="text-right">Precio</div>
            <div></div>
          </div>
          <div className="max-h-[200px] overflow-y-auto divide-y">
            {items.length === 0 && <div className="p-8 text-center text-xs text-muted-foreground">Sin items</div>}
            {items.map((item, index) => (
              <div key={index} className="p-3 grid grid-cols-[1fr,80px,40px] gap-2 items-center hover:bg-muted/10">
                <div className="text-sm font-medium truncate">{item.name}</div>
                <div className="text-sm font-bold text-right text-primary">${item.price}</div>
                <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-red-500" onClick={() => handleRemoveItem(index)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
        <div className="space-y-1">
          <Label className="text-xs font-semibold">URL de la oferta</Label>
          <Input placeholder="https://..." value={externalUrl} onChange={(e) => onExternalUrlChange(e.target.value)} />
        </div>
      </div>

      {/* Coupons Section */}
      <div className="border-t pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold">Cupones</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onCouponsChange([...coupons, { code: '', description: '' }])}
            disabled={disabled}
          >
            <Plus className="w-3 h-3 mr-1" />
            Agregar cupón
          </Button>
        </div>
        
        {coupons.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No hay cupones agregados</p>
        ) : (
          <div className="space-y-2">
            {coupons.map((coupon, idx) => (
              <div key={idx} className="flex gap-2 p-3 bg-muted/20 rounded-md border border-border/30">
                <div className="flex-1 space-y-2">
                  <Input
                    placeholder="PROMO20"
                    value={coupon.code}
                    onChange={(e) => {
                      const updated = [...coupons];
                      updated[idx].code = e.target.value;
                      onCouponsChange(updated);
                    }}
                    className="h-8 text-sm"
                    disabled={disabled}
                  />
                  <Input
                    placeholder="Descripción (opcional)"
                    value={coupon.description || ''}
                    onChange={(e) => {
                      const updated = [...coupons];
                      updated[idx].description = e.target.value;
                      onCouponsChange(updated);
                    }}
                    className="h-8 text-sm"
                    disabled={disabled}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-full shrink-0 hover:text-destructive"
                  onClick={() => onCouponsChange(coupons.filter((_, i) => i !== idx))}
                  disabled={disabled}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Checks */}
      <div className="flex gap-4 pt-2">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => onIsAffiliatedChange(!isAffiliated)}>
          <Checkbox checked={isAffiliated} />
          <Label className="text-xs cursor-pointer">Administrador</Label>
        </div>
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => onIsFreebeeChange(!isFreebee)}>
          <Checkbox checked={isFreebee} />
          <Label className="text-xs cursor-pointer">Gratis</Label>
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-3 border-t pt-4 space-y-3">
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Vigente desde</Label>
          <Input type="datetime-local" value={startAt} onChange={(e) => onStartAtChange(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-semibold">Vence el</Label>
          <Input type="datetime-local" value={expiryAt} onChange={(e) => onExpiryAtChange(e.target.value)} />
        </div>
      </div>
    </div>
  );
}

